import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Toaster } from "./ui/sonner";
import { MarketingFooter, MarketingHeader } from "./MarketingChrome";
import { FormationsCatalogSection } from "./formations/FormationsCatalogSection";
import { FormationsContactBlock } from "./formations/FormationsContactBlock";
import { FormationProgramDialog } from "./formations/FormationProgramDialog";
import { QcmSection } from "./formations/QcmSection";
import { QcmTestDialog } from "./formations/QcmTestDialog";
import { RevitLearningPathSection } from "./formations/RevitLearningPathSection";
import { cloneFormationsPageData, getSortedActiveFormations, getSortedActiveQcms, normalizeFormationsPageData } from "../lib/formationsDefaults";
import { getApiBase } from "../lib/apiBase";

export default function FormationsPage() {
  const [pageData, setPageData] = useState(() => cloneFormationsPageData());
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [selectedQcm, setSelectedQcm] = useState(null);
  const [resumeCode, setResumeCode] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        const API = getApiBase();
        const { data } = await axios.get(`${API}/api/formations-page`);
        setPageData(normalizeFormationsPageData(data));
      } catch {
        setPageData(cloneFormationsPageData());
      }
    };

    fetchPageData();
  }, []);

  // Reprise via URL ?session=CODE — auto-ouvre le dialog en mode resume.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("session");
    if (code) setResumeCode(code.trim().toUpperCase());
  }, []);

  const closeAll = () => {
    setSelectedQcm(null);
    setResumeCode(null);
    // Nettoie l'URL si on était en mode resume
    const params = new URLSearchParams(window.location.search);
    if (params.has("session")) {
      params.delete("session");
      const newSearch = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`);
    }
  };

  const handleResumeByCode = (code) => {
    setSelectedQcm(null);
    setResumeCode(code.trim().toUpperCase());
  };

  const formations = useMemo(() => getSortedActiveFormations(pageData), [pageData]);
  const qcms = useMemo(() => getSortedActiveQcms(pageData), [pageData]);

  return (
    <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(190,242,100,0.14),_transparent_24%),linear-gradient(180deg,_#fbfdf8_0%,_#f8fcf3_44%,_#ffffff_100%)]" data-testid="formations-page">
      <MarketingHeader />

      <main>
        <section className="relative overflow-hidden px-4 pb-8 pt-12 sm:px-6 lg:px-8 lg:pb-10 lg:pt-20" data-testid="formations-hero-section">
          <div className="absolute inset-x-0 top-0 h-[28rem] bg-[radial-gradient(circle_at_top,_rgba(45,212,191,0.12),_transparent_42%)]" />
          <div className="absolute left-[8%] top-[24%] h-44 w-44 rounded-full bg-[#d9f99d]/30 blur-3xl" />
          <div className="absolute right-[10%] top-[16%] h-56 w-56 rounded-full bg-[#99f6e4]/25 blur-3xl" />

          <div className="relative mx-auto max-w-7xl text-center">
            <div className="mx-auto inline-flex rounded-full border border-[#d8ece4] bg-white/92 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#0f766e] shadow-[0_16px_40px_-32px_rgba(15,118,110,0.5)]" data-testid="formations-hero-eyebrow">
              Formations BIM & logiciels métiers — tarifs HT
            </div>
            <h1 className="bim-display mx-auto mt-8 max-w-5xl text-4xl font-semibold leading-[1.02] tracking-tight text-slate-950 sm:text-5xl lg:text-6xl" data-testid="formations-hero-title">
              Une page claire pour comprendre le parcours Revit, comparer les formats et choisir la bonne formation.
            </h1>
            <p className="mx-auto mt-8 max-w-3xl text-base leading-8 text-slate-600 sm:text-lg" data-testid="formations-hero-description">
              Bimcubes rassemble ici ses formations principales, des tests QCM gratuits et un point de contact simple pour orienter votre inscription.
            </p>
          </div>
        </section>

        <RevitLearningPathSection learningPath={pageData.learningPath} />
        <FormationsCatalogSection formations={formations} onSelectFormation={setSelectedFormation} />
        <QcmSection onOpenQcm={setSelectedQcm} onResumeByCode={handleResumeByCode} qcms={qcms} />
        <FormationsContactBlock contactBlock={pageData.contactBlock} />
      </main>

      <FormationProgramDialog formation={selectedFormation} onClose={() => setSelectedFormation(null)} />
      <QcmTestDialog onClose={closeAll} qcm={selectedQcm} resumeCode={resumeCode} />
      <MarketingFooter />
      <Toaster closeButton position="top-center" />
    </div>
  );
}